import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { phCapture } from '@/lib/analytics/posthog';
import { resetBookCheckoutCancelled } from '../api';
import { useBookShellContext } from '../book-shell-context';
import { BestellungScreen } from '../components/bestellung/BestellungScreen';

/**
 * Book-funnel PostHog helper — fires a phCapture event once per scope
 * per browser session/orderId. `phCapture` returns `false` when
 * PostHog is not yet initialised (statistics consent not granted), so
 * the sentinel is only written on a real emission — the event will
 * fire later when the customer consents. All storage access is
 * try/catch'd; storage-access failures and phCapture errors are
 * non-fatal. NEVER include PII (child/cast names, story text, photos,
 * emails) in the `properties` payload.
 */
function fireBookAnalytics(
  event: string,
  dedupKey: string,
  storage: 'session' | 'local',
  properties?: Record<string, unknown>,
): void {
  try {
    const store = storage === 'session' ? window.sessionStorage : window.localStorage;
    if (store.getItem(dedupKey) === '1') return;
    if (phCapture(event, properties)) {
      store.setItem(dedupKey, '1');
    }
  } catch {
    // storage access failures + phCapture errors are non-fatal
  }
}

/**
 * 6-3 Bildschirm 5 route — `/book/:orderId/bestellung`. Reads the
 * envelope from the shared shell context (same pattern as BookRoute).
 *
 * Status guard: renders BestellungScreen when status is
 * `preview_ready` OR `checkout_pending` (re-entry after Stripe
 * abandonment). Any other status → replace-navigate to
 * `/book/:orderId` so BookRoute re-dispatches to the correct screen.
 *
 * A re-entry from Stripe abandonment lands here with
 * `status='checkout_pending'` — BestellungScreen's `handleKaufen`
 * (inside BestellungReviewScreen) calls createBookCheckout again;
 * the service's duplicate-guard detects the still-open session and
 * returns the existing checkoutUrl (idempotent resume), so the
 * customer picks up where they left off.
 *
 * Cancel-return branch (Sebastian ruling 2026-09-08 evening): Stripe
 * redirects here with `?checkout=cancelled` when the customer clicks
 * Stripe's cancel affordance. The mount effect fires
 * POST /checkout-cancelled once — race-safe rollback checkout_pending
 * → preview_ready — then refetches the envelope so the screen re-
 * renders with the fresh status, then strips the query param so a
 * reload does not re-fire. The reset is idempotent + ownership-scoped
 * on the server; the mount-once guard is a client-side cost-saver,
 * not a correctness gate.
 *
 * ## Reload-safe review phase (Sebastian ruling 2026-09-09
 * Bestellübersicht slice, defense-in-depth revision)
 *
 * This route owns the URL param logic for the two-phase flow. The
 * customer's review phase + picked sales-unit slug live on the URL
 * via `?schritt=uebersicht&unit=<slug>` so a cold-reload of that
 * URL brings the customer back to the review screen instead of
 * dropping them to format-select.
 *
 * The URL params are validated HERE, after the envelope is loaded,
 * against `envelope.orderableSalesUnits`. Only when the slug
 * resolves to an actual orderable unit does the route hydrate
 * `BestellungScreen` with `initialPhase='review'`. When the slug
 * cannot be resolved (stale URL, hand-edited param, or catalog
 * change since the URL was written), the params are stripped in a
 * side-effect and the screen renders in the default select phase —
 * NEVER null.
 *
 * The prior design read the URL params inside `BestellungScreen`
 * itself. That was the leaf shape that made the 2026-09-09 blank-
 * screen incident possible: a leaf that combined URL-param reading
 * with envelope-derived data was fragile against upstream wiring
 * bugs (specifically the `useMatch('/book/:orderId')` exact-match
 * pattern on `BookShell` that skipped the envelope fetch on cold-
 * boot to nested paths). Concentrating the resolution here means
 * any "params can't be resolved" state has a single explicit
 * branch that falls back to select layout instead of a leaf
 * silently returning null.
 */
const PARAM_SCHRITT = 'schritt';
const PARAM_UNIT = 'unit';
const SCHRITT_UEBERSICHT = 'uebersicht';

export function BestellungRoute() {
  const { orderId } = useParams<{ orderId: string }>();
  const { envelope, loading, error, refetch } = useBookShellContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = envelope?.status;
  const canRender = status === 'preview_ready' || status === 'checkout_pending';

  const checkoutFlag = searchParams.get('checkout');
  const paramSchritt = searchParams.get(PARAM_SCHRITT);
  const paramUnit = searchParams.get(PARAM_UNIT);

  // Validate paramUnit against orderableSalesUnits — the guard is
  // safe against an envelope with an empty catalog because
  // orderableSalesUnits is a stable typed array from the envelope
  // projection. If envelope is null (still loading), the memo
  // resolves to null; the loading branch below returns null before
  // it matters.
  const paramUnitMatch = useMemo(() => {
    if (paramUnit === null || envelope === null) return null;
    return envelope.orderableSalesUnits.find((u) => u.slug === paramUnit) ?? null;
  }, [paramUnit, envelope]);

  const wantsReview = paramSchritt === SCHRITT_UEBERSICHT;
  const reviewIsResolvable = wantsReview && paramUnitMatch !== null;
  const paramsAreStale = wantsReview && paramUnitMatch === null && envelope !== null && canRender;

  const resetFiredRef = useRef(false);

  useEffect(() => {
    if (checkoutFlag !== 'cancelled' || orderId === undefined || resetFiredRef.current) {
      return;
    }
    resetFiredRef.current = true;
    void (async () => {
      try {
        await resetBookCheckoutCancelled(orderId);
      } catch {
        // Ownership mismatch / network drop / missing row — swallow.
        // The customer sees the current envelope regardless; the
        // param strip below still runs so a reload does not re-fire.
      }
      refetch();
      // Strip the flag so a page reload does not re-fire the reset.
      // `replace: true` avoids adding a history entry.
      setSearchParams({}, { replace: true });
    })();
  }, [checkoutFlag, orderId, refetch, setSearchParams]);

  useEffect(() => {
    if (envelope !== null && !canRender && orderId !== undefined) {
      navigate(`/book/${orderId}`, { replace: true });
    }
  }, [envelope, canRender, navigate, orderId]);

  // Strip stale review-phase params once envelope loads and reveals
  // the referenced unit is not orderable. Fires exactly once per
  // stale-param arrival: after the strip, `paramSchritt` becomes
  // null, the effect condition is false, no re-fire. Preserves any
  // OTHER query params the customer's URL might carry (e.g., a
  // future analytics tag) — Object.fromEntries walks searchParams,
  // then delete removes only the two we own.
  useEffect(() => {
    if (!paramsAreStale) return;
    const next = new URLSearchParams(searchParams);
    next.delete(PARAM_SCHRITT);
    next.delete(PARAM_UNIT);
    setSearchParams(next, { replace: true });
  }, [paramsAreStale, searchParams, setSearchParams]);

  const handleEnterReview = useCallback(
    (slug: string) => {
      const next = new URLSearchParams(searchParams);
      next.set(PARAM_SCHRITT, SCHRITT_UEBERSICHT);
      next.set(PARAM_UNIT, slug);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleExitReview = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(PARAM_SCHRITT);
    next.delete(PARAM_UNIT);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Book-funnel analytics — event 8 (`book_checkout_started`). Fires
  // BEFORE the createBookCheckout request goes out. Callback threaded
  // down through BestellungScreen → BestellungReviewScreen so the
  // fire site sits at the click handler; order-scoped dedup via
  // localStorage. NEVER include PII (child/cast names, addresses).
  const handleCheckoutStart = useCallback(
    (slug: string | null) => {
      if (orderId === undefined) return;
      fireBookAnalytics(
        'book_checkout_started',
        `book_checkout_started_${orderId}`,
        'local',
        { book_order_id: orderId, sales_unit: slug },
      );
    },
    [orderId],
  );

  if (error !== null) {
    // BookShell's own error surface handles rendering; this route
    // just guards against dispatching a screen against a null
    // envelope.
    return null;
  }

  if (loading || envelope === null || !canRender) {
    return null;
  }

  // `initialSelectedSlug` is present-or-omitted (never present-as-
  // undefined) — the prop is declared `?: string` on BestellungScreen
  // and `pwa/tsconfig.app.json` has `exactOptionalPropertyTypes: true`,
  // so passing an explicit `undefined` to a `?: string` prop is a
  // TS2375 error under `tsc -b` (the check Cloudflare runs; caught
  // 2026-09-09 evening on the failed 5d70c30 CF build). Conditional
  // spread keeps the prop shape strict AND presents a concrete
  // string in the branch that carries a resolved unit.
  return (
    <BestellungScreen
      envelope={envelope}
      initialPhase={reviewIsResolvable ? 'review' : 'select'}
      {...(reviewIsResolvable ? { initialSelectedSlug: paramUnitMatch.slug } : {})}
      onEnterReview={handleEnterReview}
      onExitReview={handleExitReview}
      onCheckoutStart={handleCheckoutStart}
      /*
       * Sebastian ruling 2026-09-25 (E2E-test case D). When the
       * balance-only checkout succeeds server-side, refetch the
       * shell envelope so BookRoute sees the fresh status. The
       * effect at line 148-152 above then navigates automatically
       * to `/book/:orderId` (fresh status → !canRender → nav) —
       * BookRoute renders the post-payment wait screen from the
       * updated envelope. No trip through /erfolg, no stale-preview
       * flash, no "Kaufen looked like it failed" moment.
       */
      onPaidWithBalance={refetch}
    />
  );
}
